<template>
  <div class="col-md-12 mb-6">
    <label class="fs-6 fw-bold form-label mt-3">
      {{ attribute.label }} <span class="text-danger" v-if="attribute.required">*</span>
    </label>

    <div class="prenatal-panel rounded p-4">
      <div v-if="!available" class="text-muted fs-7">
        <i class="bi bi-lock me-1"></i>
        O módulo pré-natal não está disponível no seu plano ou perfil de acesso.
      </div>

      <div v-else-if="!professionalId || !patientId || !placeId" class="text-muted fs-7">
        Não foi possível identificar a paciente ou o local deste registro.
      </div>

      <PrenatalWorkspace
          v-else
          ref="workspace"
          :professional-id="professionalId"
          :patient-id="patientId"
          :place-id="placeId"
          :readonly="disabled"
          :initial-pregnancy-id="panelState.id_pregnancy"
          :leading-tab="visitTab"
          @loaded="onPregnancyLoaded">
        <template #leading="{ detail }">
          <div v-for="alert in pregnancyAlerts" :key="'pa-' + alert.code + alert.message"
               class="alert py-2 px-3 mb-2 fs-7" :class="alertClass(alert.severity)">
            <i class="bi bi-exclamation-triangle me-1"></i>{{ alert.message }}
          </div>

          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="fw-bolder fs-6">
              Consulta pré-natal
              <span class="text-muted fw-normal fs-7 ms-2" v-if="visitGestationalAge !== null">IG na consulta: {{ formatGestationalAge(visitGestationalAge) }}</span>
            </div>
            <span v-if="visit" class="badge badge-light-success"><i class="bi bi-check2 me-1"></i>Registrada</span>
          </div>

          <div class="row g-3">
            <div class="col-md-3 col-6">
              <label class="form-label fs-7">Data</label>
              <input type="date" class="form-control form-control-sm" :class="{ 'is-invalid': errors.visit_date }"
                     v-model="form.visit_date" :max="today" :disabled="readonly">
              <small class="text-danger" v-if="errors.visit_date">{{ errors.visit_date }}</small>
            </div>
            <div class="col-md-3 col-6">
              <label class="form-label fs-7">Peso (kg)</label>
              <input type="text" inputmode="decimal" class="form-control form-control-sm" :class="{ 'is-invalid': errors.weight_kg }"
                     v-model="form.weight_kg" :disabled="readonly">
              <small class="text-danger" v-if="errors.weight_kg">{{ errors.weight_kg }}</small>
            </div>
            <div class="col-md-3 col-6">
              <label class="form-label fs-7">PA (mmHg)</label>
              <div class="input-group input-group-sm">
                <input type="number" class="form-control" :class="{ 'is-invalid': errors.systolic }" v-model="form.systolic"
                       placeholder="PAS" :disabled="readonly">
                <span class="input-group-text">/</span>
                <input type="number" class="form-control" :class="{ 'is-invalid': errors.diastolic }" v-model="form.diastolic"
                       placeholder="PAD" :disabled="readonly">
              </div>
              <small class="text-danger" v-if="errors.systolic || errors.diastolic">{{ errors.systolic || errors.diastolic }}</small>
            </div>
            <div class="col-md-3 col-6">
              <label class="form-label fs-7">Altura uterina (cm)</label>
              <input type="text" inputmode="decimal" class="form-control form-control-sm" :class="{ 'is-invalid': errors.fundal_height_cm }"
                     v-model="form.fundal_height_cm" :disabled="readonly">
              <small class="text-danger" v-if="errors.fundal_height_cm">{{ errors.fundal_height_cm }}</small>
            </div>

            <template v-if="detail.fetus_count <= 1">
              <div class="col-md-3 col-6">
                <label class="form-label fs-7">BCF (bpm)</label>
                <input type="number" class="form-control form-control-sm" :class="{ 'is-invalid': errors.fetal_heart_rate }"
                       v-model="form.fetal_heart_rate" :disabled="readonly">
                <small class="text-danger" v-if="errors.fetal_heart_rate">{{ errors.fetal_heart_rate }}</small>
              </div>
              <div class="col-md-3 col-6">
                <label class="form-label fs-7">Apresentação</label>
                <select class="form-select form-select-sm" v-model="form.fetal_presentation" :disabled="readonly">
                  <option value="">—</option>
                  <option v-for="option in presentationOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                </select>
              </div>
            </template>

            <div class="col-md-3 col-6">
              <label class="form-label fs-7">Edema</label>
              <select class="form-select form-select-sm" v-model="form.edema" :disabled="readonly">
                <option value="">—</option>
                <option v-for="option in edemaOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
            </div>
            <div class="col-md-3 col-6">
              <label class="form-label fs-7">Movimentação fetal</label>
              <select class="form-select form-select-sm" v-model="form.fetal_movement" :disabled="readonly">
                <option value="">—</option>
                <option v-for="option in movementOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
            </div>

            <div class="col-12" v-if="detail.fetus_count > 1">
              <div class="table-responsive">
                <table class="table table-sm table-row-dashed align-middle mb-0">
                  <thead>
                  <tr class="text-muted fs-8 text-uppercase">
                    <th>Feto</th>
                    <th>BCF (bpm)</th>
                    <th>Apresentação</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(fetus, index) in form.fetuses" :key="'fetus-' + fetus.fetus_number">
                    <td class="fw-bold fs-7">{{ fetusLabel(fetus.fetus_number) }}</td>
                    <td>
                      <input type="number" class="form-control form-control-sm"
                             :class="{ 'is-invalid': errors[`fetuses.${index}.fetal_heart_rate`] }"
                             v-model="fetus.fetal_heart_rate" :disabled="readonly">
                      <small class="text-danger" v-if="errors[`fetuses.${index}.fetal_heart_rate`]">{{ errors[`fetuses.${index}.fetal_heart_rate`] }}</small>
                    </td>
                    <td>
                      <select class="form-select form-select-sm" v-model="fetus.fetal_presentation" :disabled="readonly">
                        <option value="">—</option>
                        <option v-for="option in presentationOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                      </select>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <small class="text-danger" v-if="errors.fetuses">{{ errors.fetuses }}</small>
            </div>

            <div class="col-12">
              <label class="form-label fs-7">Observações</label>
              <textarea class="form-control form-control-sm" rows="2" v-model="form.notes" :disabled="readonly"></textarea>
            </div>
          </div>

          <div v-for="alert in visitAlerts" :key="'va-' + alert.code + (alert.fetus_number ?? '')"
               class="alert py-2 px-3 mt-3 mb-0 fs-7" :class="alertClass(alert.severity)">
            <i class="bi bi-exclamation-octagon me-1"></i>{{ alert.message }}
          </div>

          <div class="d-flex justify-content-end mt-3" v-if="!readonly">
            <button type="button" class="btn btn-sm btn-primary" :disabled="saving" @click="saveVisit">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              {{ visit ? 'Atualizar consulta' : 'Salvar consulta' }}
            </button>
          </div>
        </template>
      </PrenatalWorkspace>
    </div>
  </div>
</template>

<script>
import http from '../../../helpers/http';
import Alert from '../../../helpers/alert';
import PrenatalWorkspace from './PrenatalWorkspace.vue';
import { canStorePrenatal, canUpdatePrenatal, prenatalAvailable } from './prenatalAccess';
import {
  EDEMA_OPTIONS,
  FETAL_MOVEMENT_OPTIONS,
  PRESENTATION_OPTIONS,
  alertClass,
  buildVisitPayload,
  diffDays,
  emptyVisitForm,
  fetusLabel,
  formatGestationalAge,
  parsePanelState,
  pregnancyUrl,
  sortAlerts,
  todayIso,
  validationErrors,
  visitToForm,
} from './prenatal';

export default {
  name: 'PrenatalPanelInput',
  emits: ['onChange'],
  components: { PrenatalWorkspace },
  props: {
    attribute: { type: Object, required: true },
    disabled: Boolean,
    schedule: { type: Object, default: null },
  },
  data() {
    return {
      available: prenatalAvailable(),
      canStore: canStorePrenatal(),
      canUpdate: canUpdatePrenatal(),
      saving: false,
      pregnancy: null,
      visit: null,
      formKey: null,
      form: emptyVisitForm(todayIso()),
      errors: {},
      visitAlerts: [],
      pregnancyAlerts: [],
      today: todayIso(),
      visitTab: { id: 'visit', label: 'Consulta', icon: 'bi bi-clipboard2-pulse' },
      presentationOptions: PRESENTATION_OPTIONS,
      edemaOptions: EDEMA_OPTIONS,
      movementOptions: FETAL_MOVEMENT_OPTIONS,
    };
  },
  computed: {
    panelState() {
      return parsePanelState(this.attribute.text);
    },
    professionalId() {
      return this.schedule?.id_prof ?? this.schedule?.availability?.id_prof ?? this.$store.state.auth.user?.id_prof;
    },
    patientId() {
      return this.schedule?.id_patient;
    },
    placeId() {
      return this.schedule?.id_place ?? this.$store.state.auth.place?.id_place;
    },
    readonly() {
      return this.disabled || (this.visit ? !this.canUpdate : !this.canStore) || this.pregnancy?.status !== 'active';
    },
    visitGestationalAge() {
      const days = diffDays(this.pregnancy?.reference_lmp_date, this.form.visit_date);

      return days !== null && days >= 0 ? days : null;
    },
  },
  methods: {
    alertClass,
    fetusLabel,
    formatGestationalAge,
    findVisit(detail) {
      const visits = detail.visits ?? [];
      const visitId = this.visit?.id ?? this.panelState.id_prenatal_visit;

      return visits.find((visit) => visit.id === visitId)
        ?? visits.find((visit) => this.attribute.id_medrec && visit.id_medrec === this.attribute.id_medrec)
        ?? null;
    },
    onPregnancyLoaded(detail) {
      this.pregnancy = detail;

      if (!detail) {
        this.visit = null;
        this.visitAlerts = [];
        this.pregnancyAlerts = [];
        return;
      }

      this.visit = this.findVisit(detail);
      this.visitAlerts = sortAlerts(this.visit?.alerts ?? []);
      this.pregnancyAlerts = sortAlerts((detail.alerts ?? []).filter((alert) => alert.id_prenatal_visit === null));

      const formKey = `${detail.id}-${detail.fetus_count}-${this.visit?.id ?? 'new'}-${this.visit?.updated_at ?? ''}`;
      if (formKey !== this.formKey) {
        this.formKey = formKey;
        this.errors = {};
        this.form = this.visit
          ? visitToForm(this.visit, detail.fetus_count)
          : emptyVisitForm(this.today, detail.fetus_count);
      }
    },
    async saveVisit() {
      this.saving = true;
      this.errors = {};

      const url = pregnancyUrl(this.professionalId, this.patientId, this.pregnancy.id, '/visits');
      const payload = buildVisitPayload(this.form, this.pregnancy.fetus_count);

      try {
        const response = this.visit
          ? await http.put(`${url}/${this.visit.id}`, payload)
          : await http.post(url, {
            ...payload,
            id_medrec: this.attribute.id_medrec ?? null,
            id_sched: this.schedule?.id_sched ?? null,
          });

        this.visit = response.data;
        this.$emit('onChange', {
          ...this.attribute,
          text: JSON.stringify({ id_prenatal_visit: this.visit.id, id_pregnancy: this.pregnancy.id }),
        });
        await this.$refs.workspace?.reload();
        Alert.toastSuccess('Consulta pré-natal salva.');
      } catch (error) {
        this.errors = validationErrors(error);
        if (!Object.keys(this.errors).length) {
          Alert.toastError(error.response?.data?.message || 'Falha ao salvar a consulta pré-natal.');
        } else if (this.errors.id_medrec || this.errors.id_pregnancy) {
          Alert.toastError(this.errors.id_medrec || this.errors.id_pregnancy);
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.prenatal-panel {
  border: 1px solid #eff2f5;
  background: #fff;
}
</style>
